import {
  Badge,
  Button,
  Card,
  CardBody,
  Input,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@foundation/ui'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import styles from './cancellations.index.module.css'
import { Skeleton } from '@/components/Skeleton'
import { api, type Cancellation, type CancellationStatus } from '@/lib/api'
import { toast } from '@/lib/toast'

export const Route = createFileRoute('/cancellations/')({
  component: CancellationsIndexPage,
})

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'active', label: 'In Progress' },
  { value: 'won_back', label: 'Recovered' },
  { value: 'lost', label: 'Lost' },
  { value: 'paused', label: 'Paused' },
]

function formatCurrency(amount: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount / 100)
}

function getStatusBadge(status: CancellationStatus) {
  switch (status) {
    case 'won_back':
      return <Badge variant="success">Recovered</Badge>
    case 'lost':
      return <Badge variant="danger">Lost</Badge>
    case 'active':
      return <Badge variant="primary">In Progress</Badge>
    case 'paused':
      return <Badge variant="warning">Paused</Badge>
    default:
      return <Badge variant="default">{status}</Badge>
  }
}

function CancellationsIndexPage() {
  const navigate = useNavigate()
  const [cancellations, setCancellations] = useState<Cancellation[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [total, setTotal] = useState(0)
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [search, setSearch] = useState('')
  const [searchDebounced, setSearchDebounced] = useState('')

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounced(search)
      setPage(1)
    }, 300)
    return () => clearTimeout(timer)
  }, [search])

  const fetchRef = useRef(0)

  useEffect(() => {
    const fetchId = ++fetchRef.current
    api
      .listCancellations({
        page,
        per_page: 20,
        ...(statusFilter ? { status: statusFilter as CancellationStatus } : {}),
        ...(searchDebounced ? { search: searchDebounced } : {}),
      })
      .then((res) => {
        if (fetchId !== fetchRef.current) return
        setCancellations(res.data)
        setTotalPages(res.pagination.total_pages)
        setTotal(res.pagination.total)
      })
      .catch((err: unknown) => {
        if (fetchId !== fetchRef.current) return
        toast.error(err instanceof Error ? err.message : 'Failed to load cancellations')
      })
      .finally(() => {
        if (fetchId === fetchRef.current) setIsLoading(false)
      })
  }, [page, statusFilter, searchDebounced])

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusFilter(e.target.value)
    setPage(1)
  }

  const handleRowClick = (id: string) => {
    void navigate({ to: '/cancellations/$id', params: { id } })
  }

  if (!isLoading && cancellations.length === 0 && !statusFilter && !searchDebounced) {
    return (
      <Card className={styles.emptyState ?? ''}>
        <CardBody>
          <div className={styles.emptyIllustration ?? ''}>
            <svg
              width="140"
              height="100"
              viewBox="0 0 140 100"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect
                x="20"
                y="15"
                width="100"
                height="70"
                rx="8"
                stroke="currentColor"
                strokeWidth="2"
                opacity="0.2"
              />
              <path
                d="M35 45H105"
                stroke="currentColor"
                strokeWidth="2"
                opacity="0.15"
                strokeLinecap="round"
              />
              <path
                d="M35 55H80"
                stroke="currentColor"
                strokeWidth="2"
                opacity="0.15"
                strokeLinecap="round"
              />
              <circle
                cx="110"
                cy="25"
                r="18"
                fill="var(--color-primary)"
                opacity="0.15"
                stroke="var(--color-primary)"
                strokeWidth="2"
              />
              <path
                d="M104 25L109 30L117 20"
                stroke="var(--color-primary)"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity="0.5"
              />
              <rect
                x="35"
                y="28"
                width="30"
                height="8"
                rx="2"
                fill="var(--color-primary)"
                opacity="0.15"
              />
            </svg>
          </div>
          <h3 className={styles.emptyTitle ?? ''}>No cancellations yet</h3>
          <p className={styles.emptyDescription ?? ''}>
            Cancellations from your Stripe subscriptions will automatically appear here once your
            account is connected. BounceBack listens for cancellation events via webhooks and begins
            your win-back sequences automatically.
          </p>
          <div className={styles.emptySteps ?? ''}>
            <div className={styles.emptyStep ?? ''}>
              <span className={styles.emptyStepIcon ?? ''}>1</span>
              <div>
                <strong>Connect Stripe</strong>
                <span className={styles.emptyStepDesc ?? ''}>
                  Add your Stripe webhook endpoint in Settings
                </span>
              </div>
            </div>
            <div className={styles.emptyStep ?? ''}>
              <span className={styles.emptyStepIcon ?? ''}>2</span>
              <div>
                <strong>Set up a sequence</strong>
                <span className={styles.emptyStepDesc ?? ''}>
                  Create email templates to send to churned users
                </span>
              </div>
            </div>
            <div className={styles.emptyStep ?? ''}>
              <span className={styles.emptyStepIcon ?? ''}>3</span>
              <div>
                <strong>Recover revenue</strong>
                <span className={styles.emptyStepDesc ?? ''}>
                  Track win-backs and recovered MRR here
                </span>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>
    )
  }

  return (
    <>
      {/* Filters */}
      <div className={styles.filters}>
        <div className={styles.searchInput}>
          <Input
            type="search"
            placeholder="Search by customer name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className={styles.filterSelect}>
          <Select value={statusFilter} onChange={handleStatusChange} options={STATUS_OPTIONS} />
        </div>
      </div>

      {/* Table */}
      <div className={styles.tableWrapper}>
        <Card>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeader>Customer</TableHeader>
                <TableHeader>Plan</TableHeader>
                <TableHeader>MRR</TableHeader>
                <TableHeader>Cancelled</TableHeader>
                <TableHeader>Reason</TableHeader>
                <TableHeader>Status</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <>
                  {[1, 2, 3, 4, 5].map((i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <Skeleton width="70%" height="1rem" />
                      </TableCell>
                      <TableCell>
                        <Skeleton width="60%" height="1rem" />
                      </TableCell>
                      <TableCell>
                        <Skeleton width="50%" height="1rem" />
                      </TableCell>
                      <TableCell>
                        <Skeleton width="80%" height="1rem" />
                      </TableCell>
                      <TableCell>
                        <Skeleton width="60%" height="1rem" />
                      </TableCell>
                      <TableCell>
                        <Skeleton width="50%" height="1rem" />
                      </TableCell>
                    </TableRow>
                  ))}
                </>
              ) : cancellations.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}
                  >
                    No cancellations match your filters.
                  </TableCell>
                </TableRow>
              ) : (
                cancellations.map((cancellation) => (
                  <TableRow key={cancellation.id} onClick={() => handleRowClick(cancellation.id)}>
                    <TableCell>
                      <div className={styles.customerCell}>
                        <span className={styles.customerName}>
                          {cancellation.customerName ?? 'Unknown'}
                        </span>
                        <span className={styles.customerEmail}>{cancellation.customerEmail}</span>
                      </div>
                    </TableCell>
                    <TableCell>{cancellation.productName ?? '--'}</TableCell>
                    <TableCell>
                      <span className={styles.mrrValue}>
                        {formatCurrency(cancellation.mrr, cancellation.currency)}
                      </span>
                    </TableCell>
                    <TableCell>{new Date(cancellation.cancelledAt).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <span className={styles.reasonText}>{cancellation.surveyReason ?? '--'}</span>
                    </TableCell>
                    <TableCell>{getStatusBadge(cancellation.status)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className={styles.pagination}>
          <span className={styles.paginationInfo}>
            Showing {cancellations.length} of {total} cancellations
          </span>
          <div className={styles.paginationButtons}>
            <Button
              variant="secondary"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <span className={styles.paginationInfo}>
              Page {page} of {totalPages}
            </span>
            <Button
              variant="secondary"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </>
  )
}
